/**
 * ConnectionsPage — Connections / Tool Registry (Admin/Owner only).
 *
 * Shows the configuration status of each external integration and provides
 * a live connectivity test for the OpenClaw runtime.
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { connectionsApi } from "../services/api";
import { riskGradeLabel } from "../components/riskGradeVocabulary";
import { operationTypeLabel } from "../components/operationTypeVocabulary";
import ProviderOnboardingPanel from "../components/ProviderOnboardingPanel";

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ ok, label }) {
  if (ok === null || ok === undefined) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
        לא ידוע
      </span>
    );
  }
  return ok ? (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
      {label || "מחובר"}
    </span>
  ) : (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
      לא מוגדר
    </span>
  );
}

// ─── ConnectionCard ───────────────────────────────────────────────────────────

function ConnectionCard({ title, icon, configured, details, testButton }) {
  return (
    <div className={`bg-white rounded-xl border shadow-sm p-5 space-y-3 ${configured ? "" : "opacity-70"}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xl" aria-hidden="true">{icon}</span>
          <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
        </div>
        <StatusBadge ok={configured} label="מוגדר" />
      </div>

      {details && (
        <div className="space-y-1">
          {details.map(({ label, value }) => (
            value != null && (
              <div key={label} className="flex items-start gap-2 text-xs">
                <span className="text-gray-500 shrink-0 w-28">{label}:</span>
                <span className="text-gray-700 font-mono break-all">{value}</span>
              </div>
            )
          ))}
        </div>
      )}

      {testButton}
    </div>
  );
}

// ─── OpenClawTestButton ───────────────────────────────────────────────────────

export function OpenClawTestButton() {
  const [result, setResult] = useState(null);

  const mutation = useMutation({
    mutationFn: connectionsApi.testOpenclaw,
    onSuccess: (data) => setResult(data),
    onError: (err) => setResult({ ok: false, error: err?.response?.data?.detail || "שגיאה" }),
  });

  // "Connection OK" (green) is only claimed when the runtime AND a node are confirmed (ready === true). A mock
  // adapter (no connection at all) or a reachable runtime whose node list could not be read (ready == null)
  // is amber: the backend returns both as ok:true.
  const unverified = Boolean(result?.ok) && result.ready !== true;

  return (
    <div className="space-y-2 border-t pt-3">
      <button
        type="button"
        onClick={() => { setResult(null); mutation.mutate(); }}
        disabled={mutation.isPending}
        className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded text-xs font-medium disabled:opacity-50 transition-colors"
      >
        {mutation.isPending ? "בודק..." : "בדוק חיבור"}
      </button>

      {result && (
        <div className={`rounded-lg border p-3 text-xs space-y-1 ${
          unverified
            ? "bg-amber-50 border-amber-200"
            : result.ok
            ? "bg-green-50 border-green-200"
            : "bg-red-50 border-red-200"
        }`}>
          {result.ok ? (
            <>
              <p className={`${unverified ? "text-amber-700" : "text-green-700"} font-medium`}>
                {result.mode === "mock"
                  ? "מצב Mock — אין חיבור אמיתי"
                  : result.ready === false
                  ? "Runtime זמין, אין Node מחובר"
                  : result.ready == null
                  ? "Runtime זמין, מוכנות ה-Node לא ידועה"
                  : "חיבור תקין"}
              </p>
              {result.version  && <p className={unverified ? "text-amber-600" : "text-green-600"}>גרסה: <span className="font-mono">{result.version}</span></p>}
              {result.duration_ms && <p className={unverified ? "text-amber-500" : "text-green-500"}>{result.duration_ms} ms</p>}
              {result.nodes && (
                <p className={unverified ? "text-amber-600" : "text-green-600"}>
                  Nodes: {result.nodes.connected} מחוברים / {result.nodes.paired} משויכים / {result.nodes.pending} ממתינים
                </p>
              )}
              {result.message  && <p className={unverified ? "text-amber-600" : "text-green-600"}>{result.message}</p>}
            </>
          ) : (
            <>
              <p className="text-red-700 font-medium">חיבור נכשל</p>
              <p className="text-red-600 font-mono break-all">{result.error}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ToolRegistrySection ─────────────────────────────────────────────────────

const TOOL_STATUS_LABELS = {
  requires_connected_node: "דורש Node מחובר",
  available: "זמין",
  disabled: "מושבת",
};

export function ToolRegistrySection({ tools = [] }) {
  if (!tools.length) return null;

  return (
    <div className="md:col-span-2 bg-white rounded-xl border shadow-sm p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-semibold text-gray-800 text-sm">Tool Registry</h2>
        <span className="text-xs text-gray-500">{tools.length} כלים</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-separate border-spacing-y-1">
          <thead>
            <tr className="text-gray-500">
              <th className="text-right font-medium px-2 py-1">כלי</th>
              <th className="text-right font-medium px-2 py-1">Runtime</th>
              <th className="text-right font-medium px-2 py-1">פעולה</th>
              <th className="text-right font-medium px-2 py-1">Risk</th>
              <th className="text-right font-medium px-2 py-1">אישור</th>
              <th className="text-right font-medium px-2 py-1">Audit</th>
              <th className="text-right font-medium px-2 py-1">מצב</th>
            </tr>
          </thead>
          <tbody>
            {tools.map((tool) => (
              <tr key={tool.id} className="bg-gray-50">
                <td className="px-2 py-2 font-mono text-gray-800 rounded-r-lg">{tool.id}</td>
                <td className="px-2 py-2 text-gray-600">{tool.runtime}</td>
                <td className="px-2 py-2 text-gray-600">{operationTypeLabel(tool.operation_type)}</td>
                <td className="px-2 py-2 text-gray-600">{riskGradeLabel(tool.risk_level)}</td>
                <td className="px-2 py-2">
                  <span className={`px-2 py-0.5 rounded-full border ${
                    tool.approval_required
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-green-50 text-green-700 border-green-200"
                  }`}>
                    {tool.approval_required ? "נדרש" : "לא נדרש"}
                  </span>
                </td>
                <td className="px-2 py-2 text-gray-600">{tool.audit_required ? "כן" : "לא"}</td>
                <td className="px-2 py-2 rounded-l-lg">
                  <div className="space-y-0.5">
                    <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                      {TOOL_STATUS_LABELS[tool.status] || tool.status}
                    </span>
                    {tool.readiness_dependency && (
                      <div className="text-[11px] text-gray-500">{tool.readiness_dependency}</div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ConnectionsPage ──────────────────────────────────────────────────────────

export default function ConnectionsPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["connectionsHealth"],
    queryFn: connectionsApi.health,
  });

  return (
    <div className="space-y-5 max-w-4xl mx-auto" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">חיבורים ורישום כלים</h1>
        <p className="text-sm text-gray-500 mt-0.5">מצב האינטגרציות החיצוניות של המערכת</p>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
        </div>
      )}

      {isError && (
        <div className="py-8 text-center text-red-600 text-sm">לא ניתן לטעון מצב חיבורים</div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* OpenClaw */}
          <ConnectionCard
            title="OpenClaw Runtime"
            icon="🦞"
            configured={data.openclaw?.configured}
            details={[
              { label: "מצב",    value: data.openclaw?.mode_label },
              { label: "שרת",    value: data.openclaw?.host },
              { label: "משתמש",  value: data.openclaw?.user },
              { label: "מפתח",   value: data.openclaw?.key_path },
            ]}
            testButton={<OpenClawTestButton />}
          />

          {/* TeamViewer */}
          <ConnectionCard
            title="TeamViewer"
            icon="🖥️"
            configured={data.teamviewer?.configured}
            details={[
              { label: "API Token", value: data.teamviewer?.token_prefix },
            ]}
          />

          {/* Microsoft 365 */}
          <ConnectionCard
            title="Microsoft 365 / Graph"
            icon="☁️"
            configured={data.ms365?.configured}
            details={[
              { label: "ארגון",         value: data.ms365?.tenant_id_prefix },
              { label: "תיבת דואר",     value: data.ms365?.mailbox },
            ]}
          />

          {/* AI Analysis */}
          <ConnectionCard
            title="ניתוח AI"
            icon="🧠"
            configured={data.ai_analysis?.configured}
            details={[
              { label: "ספק",       value: data.ai_analysis?.provider },
              { label: "מודל",      value: data.ai_analysis?.model },
              { label: "Anthropic", value: data.ai_analysis?.anthropic_key_set ? "מוגדר" : "לא מוגדר" },
              { label: "OpenAI",    value: data.ai_analysis?.openai_key_set    ? "מוגדר" : "לא מוגדר" },
            ]}
          />

          <ToolRegistrySection tools={data.tool_registry || []} />

          {/* PROD-PROVIDERGUI-023: prepare and validate a provider draft. The panel is self-gated on the
              admin/owner roles the two routes require, and it provisions nothing. */}
          <ProviderOnboardingPanel />
        </div>
      )}
    </div>
  );
}
